import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateCompany } from '@/modules/core/companies/hooks/useCompanies';
import type {
  Company,
  CompanyType,
  CompanyStatus,
  UpdateCompanyDto,
} from '@/modules/core/companies/types/company.types';
import {
  COMPANY_TYPE_CONFIG,
  COMPANY_STATUS_CONFIG,
} from '@/modules/core/companies/types/company.types';

interface CompanyInfoTabProps {
  company: Company;
}

export function CompanyInfoTab({ company }: CompanyInfoTabProps) {
  const [formData, setFormData] = useState<UpdateCompanyDto>({});
  const updateCompany = useUpdateCompany();

  useEffect(() => {
    setFormData({
      name: company.name,
      companyType: company.companyType,
      status: company.status,
      address: company.address ?? undefined,
      city: company.city ?? undefined,
      postalCode: company.postalCode ?? undefined,
      country: company.country ?? 'FR',
      phone: company.phone ?? undefined,
      email: company.email ?? undefined,
      website: company.website ?? undefined,
    });
  }, [company]);

  const handleChange = (field: keyof UpdateCompanyDto, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value || undefined }));
  };

  const handleSave = () => {
    updateCompany.mutate(
      { id: company.id, data: formData },
      {
        onSuccess: () => toast.success('Fiche société mise à jour'),
        onError: () => toast.error('Erreur lors de la mise à jour'),
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Identification */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Identification
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="name">Nom</Label>
            <Input
              id="name"
              value={formData.name ?? ''}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>SIRET</Label>
            <Input
              value={company.siret ?? '—'}
              readOnly
              disabled
              className="bg-muted font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Le SIRET ne peut pas être modifié après la création
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyType">Type</Label>
            <Select
              value={formData.companyType ?? 'prospect'}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, companyType: value as CompanyType }))
              }
            >
              <SelectTrigger id="companyType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(COMPANY_TYPE_CONFIG) as CompanyType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {COMPANY_TYPE_CONFIG[t].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="status">Statut</Label>
            <Select
              value={formData.status ?? 'active'}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, status: value as CompanyStatus }))
              }
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(COMPANY_STATUS_CONFIG) as CompanyStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {COMPANY_STATUS_CONFIG[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Adresse */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Adresse
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address">Adresse</Label>
            <Input
              id="address"
              value={formData.address ?? ''}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="postalCode">Code postal</Label>
            <Input
              id="postalCode"
              value={formData.postalCode ?? ''}
              onChange={(e) => handleChange('postalCode', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">Ville</Label>
            <Input
              id="city"
              value={formData.city ?? ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Pays (ISO 2)</Label>
            <Input
              id="country"
              value={formData.country ?? 'FR'}
              onChange={(e) => handleChange('country', e.target.value.toUpperCase())}
              maxLength={2}
            />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="space-y-4">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Contact
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Téléphone</Label>
            <Input
              id="phone"
              value={formData.phone ?? ''}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email ?? ''}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="website">Site web</Label>
            <Input
              id="website"
              placeholder="https://..."
              value={formData.website ?? ''}
              onChange={(e) => handleChange('website', e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="flex justify-end pt-2 border-t">
        <Button onClick={handleSave} disabled={updateCompany.isPending}>
          {updateCompany.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
